import { useMutation } from '@tanstack/react-query';
import updateProjectName from 'api/project/updateProjectName';
import { REGEXP } from 'constants/commonConstants';
import useProject from 'hooks/useProject';
import { useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';

const useProjectName = () => {
  const project = useProject();

  const inputRef = useRef<HTMLInputElement>(null);

  const [projectName, setProjectName] = useState(project.data!.projectName);
  const [isEditMode, setIsEditMode] = useState(false);

  const updateMutation = useMutation({
    mutationFn: updateProjectName,
    onSuccess: (data, { newProjectName }) => {
      setIsEditMode(false);
      project.update({ projectName: newProjectName, ...(data.generateCheck && { code: data.code }) });

      if (!data.generateCheck) {
        toast.error('코드 변경에 실패했습니다.');
      }
    },
    onError: (error: any) => {
      if (error?.message === '21001') {
        inputRef.current?.focus();
        toast.error('이미 존재하는 프로젝트명 입니다.');
      } else {
        toast.error('프로젝트 이름 변경에 실패했습니다.');
      }
    },
  });

  const handleEditModeClick = () => {
    setIsEditMode(true);
  };

  const handleNameChange = (event: React.FormEvent<HTMLInputElement>) => {
    setProjectName(event.currentTarget.value);
  };

  const handleInputBlur = () => {
    if (project.data?.projectName === projectName) {
      setIsEditMode(false);
      return;
    }

    if (projectName.match(REGEXP.PROJECT_NAME)) {
      updateMutation.mutate({
        ownerName: project.data!.ownerName,
        projectName: project.data!.projectName,
        newProjectName: projectName,
      });
    } else {
      inputRef?.current?.focus();
      toast.error('프로젝트 이름은 2-50자의 영문, 숫자, -, _의 조합으로 이루어져야합니다.');
    }
  };

  useEffect(() => {
    if (isEditMode) {
      inputRef.current?.focus();
    }
  }, [inputRef, isEditMode]);

  return {
    inputRef,
    projectName,
    isEditMode,
    framework: project.data?.framework,
    frameworkVersion: project.data?.frameworkVersion,
    onEditModeClick: handleEditModeClick,
    onNameChange: handleNameChange,
    onInputBlur: handleInputBlur,
  };
};

export default useProjectName;
